import { useEffect, useMemo, useState } from "react";
import omit from "lodash/fp/omit";
import classNames from "classnames";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import useSorting from "@rio-cloud/rio-uikit/useSorting";
import useTableSelection from "@rio-cloud/rio-uikit/useTableSelection";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import Toolbar, { ColumnDetails, ColumnDetailsMap, ColumnLabel } from "./Toolbar";
import Table from "./Table";

export interface TableDataProps {
  id: number;
  date: string;
  name: string;
  type?: string;
  expenseType?: string;
  categoryName?: string;
  categoryId?: number;
}

const demoColumnsDetails: ColumnDetailsMap = {};

export type TableSettingsProps = {
  columnLabels: ColumnLabel;
  defaultColumnOrder: string[];
  titleButtonModal?: string;
  viewType: TableViewTogglesViewType;
  data: TableDataProps[];
  totalData: number;
  onLoadMore: ({ size, page }: { size: number; page: number }) => void;
  handleRefresh: () => void;
  handleDownload: () => void;
  openModal?: (open: boolean) => void;
  onEdit?: (
    id: number,
    name: string,
    type?: string,
    expenseType?: string,
    categoryName?: string,
    categoryId?: number
  ) => void;
  onDelete?: (id: number) => void;
  onDeleteFile?: (id: string) => void;
  onGetFile?: (id: string) => void;
  onObservation?: (id: string) => void;
};

const TableSettings = (props: TableSettingsProps) => {
  const {
    viewType: externalViewType,
    data,
    totalData,
    onLoadMore,
    handleDownload,
    handleRefresh,
    onEdit,
    onDelete,
    onObservation,
    onDeleteFile,
    onGetFile,
    columnLabels,
    defaultColumnOrder,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [filter, setFilter] = useState<TableFilterKey[]>([]);

  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);

  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);

  const { sortedItems, sortKey, sortDirection, setSortKey, setSortDirection, toggleDirection } = useSorting(
    data,
    "date"
  );

  const { tableRef, updateRowSelection, onActiveRowChange } = useTableSelection<TableDataProps>({
    tableData: data,
    idKey: "id" as keyof TableDataProps,
  });

  useEffect(() => updateRowSelection(), [searchValue]);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);
  const handleSearchValueChange = (newSearchValue: string) => setSearchValue(newSearchValue);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (column: string, newColumnDetails: ColumnDetails) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      setSortKey(newSortBy as keyof TableDataProps);
    }
    if (newSortBy === sortKey) {
      toggleDirection();
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setSortKey(newSortBy as keyof TableDataProps);
    setSortDirection(newSortDir);
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = sortedItems.map((vehicle) => ({ ...withoutHidden(vehicle) })) as TableDataProps[];

  const searchResult = useMemo(() => {
    if (!searchValue) {
      return filteredRows;
    }
    return filteredRows.filter((row: Partial<TableDataProps>) =>
      columns.some((col) => {
        const value = row[col as keyof TableDataProps];
        if (value == null) {
          return false;
        }
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [searchValue, filteredRows, columns]);

  // Filter according to filter dialog settings
  const rows = useMemo(() => applyFilter(searchResult, filter), [searchResult, filter]);

  const tableClassNames = classNames(
    "table",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortKey,
      disabled: false,
    };
  });

  return (
    <div className="height-100pct">
      <Toolbar
        searchValue={searchValue}
        handleSearchValueChange={handleSearchValueChange}
        openModal={(open) => props.openModal && props.openModal(open)}
      />
      {sortKey && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortKey as string}
          sortOrder={sortDirection}
          onSortChange={handleCardSortChange}
        />
      )}
      <Table
        rows={rows}
        isViewTypeTable={isViewTypeTable}
        tableRef={tableRef}
        tableClassNames={tableClassNames}
        columns={columns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        sortKey={sortKey}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        onActiveRowChange={onActiveRowChange}
        onEdit={onEdit}
        onDelete={onDelete}
        onDeleteFile={onDeleteFile}
        onGetFile={onGetFile}
        onObservation={onObservation}
      />

      {rows?.length > 0 ? (
        <LoadMoreButton
          loaded={rows.length}
          total={totalData}
          onLoadMore={() =>
            onLoadMore({
              page: totalData / 10,
              size: rows.length + 10,
            })
          }
          loadMoreMessage="Carregar mais"
          noMoreMessage="Não há mais resultado"
        />
      ) : null}
    </div>
  );
};

const FILTER_KAY_MISSING_VIN = "missingVin";
type TableFilterKey = typeof FILTER_KAY_MISSING_VIN;

const applyFilter = (items: TableDataProps[], filter: string[]) => {
  const filterSet = new Set(filter);

  return items.filter((item) => {
    if (filterSet.has(FILTER_KAY_MISSING_VIN) && !item.date) {
      return false;
    }

    return true;
  });
};

export default TableSettings;
